import React, { useState, useEffect } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Actions
import { ForgotUserPassword } from '~/redux/actions/auth-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { EMAIL_REGEX } from '~/utils/constants';
import { validateForm, realTimeValidation } from '~/utils/formik-validation';
import { MESSAGES } from '~/utils/message';

// MUI Components
import { Box, Input, InputLabel, Typography, CircularProgress } from '@mui/material';
import OlcButton from '~/components/General/OlcButton';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { OnBoardCIcon } from '~/assets/images';

// Styles
import '../../index.scss';
import '../CreateAccountNew/styles.scss';
import './styles.scss';

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .matches(EMAIL_REGEX, MESSAGES.AUTH.INVALID_EMAIL)
      .required(MESSAGES.AUTH.EMAIL_REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchema,
  });

  const forgotPassword = async (event) => {
    try {
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }
      const payload = {
        email: formik.values.email,
      };
      setLoading(true);
      const request = await dispatch(ForgotUserPassword(payload));
      setLoading(false);
      if (request.message === 'success') {
        navigate('/verification');
        dispatch(success(request?.data?.message || ''));
      } else {
        dispatch(failure(request?.response?.data?.message || MESSAGES.GENERAL_ERROR));
        if (request?.response?.data?.data?.errors?.length) {
          let serverSideError = request?.response?.data?.data?.errors;
          serverSideError.forEach((error) => {
            formik.errors[error.path] = error.msg;
            formik.touched[error.path] = true;
          });
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    formik.touched.email = false;
  }, []);

  return (
    <Box className="newCreateAccountWrapper forgotPasswordPageWrapper">
      <Box className="topIconWrapper">
        <img src={OnBoardCIcon} alt="OpenLetter" />
      </Box>
      <Box className="createAccountFormWrapper forgotPasswordFormCard">
        <Box className="forgotPasswordBackButton">
          <ArrowBackIcon
            onClick={() => {
              navigate('/login');
            }}
          />
        </Box>
        <Box className="createAccountFormHeader forgotPasswordFormHeader">
          <Typography variant="h4">{MESSAGES.FORGOT_PASSWORD.TITLE}</Typography>
          <Typography>{MESSAGES.FORGOT_PASSWORD.HEADING}</Typography>
        </Box>
        <form onSubmit={forgotPassword}>
          <Box className="newInputWrapper">
            <InputLabel className="formLabel">
              {MESSAGES.FORGOT_PASSWORD.INPUT_LABEL_EMAIL}
              <span style={{ position: 'absolute', top: '-3px' }}>*</span>
            </InputLabel>
            <Input
              type="text"
              placeholder={MESSAGES.FORGOT_PASSWORD.EMAIL_PLACEHOLDER}
              autoComplete="off"
              name="email"
              onChange={formik.handleChange}
              className={`newFormInput ${
                formik.errors.email && formik.touched.email ? 'invalid' : ''
              }`}
              onBlur={realTimeValidation ? formik.handleBlur : undefined}
              value={formik.values.email}
            />
            {formik.touched.email && formik.errors.email && (
              <Typography className="errorMessageNew">
                <sup>*</sup>
                {formik.errors.email}
              </Typography>
            )}
          </Box>
          <OlcButton
            variant="primary"
            className="submitButton forgotPasswordSubmitButton"
            type="submit"
            onClick={() => {
              validateForm(formik);
            }}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '25px !important',
                  height: '25px !important',
                }}
              />
            ) : (
              MESSAGES.FORGOT_PASSWORD.SUBMIT_BUTTON
            )}
          </OlcButton>
        </form>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
